import React from 'react'

/**
 * AnomalyIcons — composite inline SVG icon strip for ObjectVersion anomaly state.
 *
 * Renders zero to four Lucide-style icons (timing, size, files, shape) with
 * severity-driven styling. Clean versions (null data) show a green check.
 */

export type AnomalyData = {
  timing?: 1 | 2
  size?: 1 | 2
  files?: 1 | 2
  shape?: 1 | 2
} | null

interface AnomalyIconsProps {
  anomalyData: AnomalyData
  size?: number
  gap?: number
}

const CATEGORY_ORDER = ['timing', 'size', 'files', 'shape'] as const
type Category = (typeof CATEGORY_ORDER)[number]

const CATEGORY_COLORS: Record<Category, string> = {
  timing: '#d97706',
  size: '#2563eb',
  files: '#dc2626',
  shape: '#7c3aed',
}

const CATEGORY_LABELS: Record<Category, string> = {
  timing: 'Timing anomaly',
  size: 'Size anomaly',
  files: 'File anomaly',
  shape: 'Shape anomaly',
}

function TimingIcon() {
  return (
    <>
      <path d="M12 6v6l4 2" />
      <path d="M20 10v5" />
      <path d="M20 19h.01" />
      <path d="M21.25 8.2A10 10 0 1 0 16 21.16" />
    </>
  )
}

function SizeIcon() {
  return (
    <>
      <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" />
      <path d="m14.5 12.5 2-2" />
      <path d="m11.5 9.5 2-2" />
      <path d="m8.5 6.5 2-2" />
      <path d="m17.5 15.5 2-2" />
    </>
  )
}

function FilesIcon() {
  return (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M9 10h6" />
      <path d="M12 7v6" />
      <path d="M9 17h6" />
    </>
  )
}

function ShapeIcon() {
  return (
    <>
      <path d="M12 3v14" />
      <path d="M5 10h14" />
      <path d="M5 21h14" />
    </>
  )
}

const CATEGORY_ICONS: Record<Category, () => React.ReactNode> = {
  timing: TimingIcon,
  size: SizeIcon,
  files: FilesIcon,
  shape: ShapeIcon,
}

export function AnomalyIcons({ anomalyData, size = 16, gap = 4 }: AnomalyIconsProps) {
  // Clean — green check
  if (!anomalyData) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#16a34a"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: 0.8 }}
      >
        <circle cx="12" cy="12" r="9" />
        <polyline points="8 12 11 15 16 9" />
      </svg>
    )
  }

  const activeCategories = CATEGORY_ORDER.filter(c => anomalyData[c] != null)

  if (activeCategories.length === 0) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#16a34a"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: 0.8 }}
      >
        <circle cx="12" cy="12" r="9" />
        <polyline points="8 12 11 15 16 9" />
      </svg>
    )
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap }}>
      {activeCategories.map(cat => {
        const severity = anomalyData[cat]!
        const color = CATEGORY_COLORS[cat]
        const label = CATEGORY_LABELS[cat]
        const Icon = CATEGORY_ICONS[cat]
        const isHigh = severity === 2

        return (
          <span
            key={cat}
            style={{ position: 'relative', display: 'inline-flex' }}
            title={`${label} — severity ${severity}`}
          >
            <svg
              width={size}
              height={size}
              viewBox="0 0 24 24"
              fill="none"
              stroke={color}
              strokeWidth={isHigh ? 2.2 : 1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ opacity: isHigh ? 1 : 0.75 }}
            >
              <Icon />
            </svg>
            {isHigh && (
              <svg
                width={7}
                height={7}
                viewBox="0 0 7 7"
                style={{ position: 'absolute', top: -2, right: -2 }}
              >
                <circle cx="3.5" cy="3.5" r="3.5" fill="#ef4444" />
              </svg>
            )}
          </span>
        )
      })}
    </span>
  )
}

/**
 * ScoreBadge — small pill showing the anomaly score (0–8).
 *
 * 0: green, 1–3: amber, 4+: red.
 */

interface ScoreBadgeProps {
  score: number
}

export function ScoreBadge({ score }: ScoreBadgeProps) {
  let bg: string
  let fg: string

  if (score === 0) {
    bg = '#34d39918'
    fg = '#34d399'
  } else if (score <= 3) {
    bg = '#f59e0b18'
    fg = '#f59e0b'
  } else {
    bg = '#ef444418'
    fg = '#ef4444'
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 20,
        borderRadius: 5,
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
        padding: '0 5px',
      }}
    >
      {score}
    </span>
  )
}
